import axios from 'axios';

// Default API URL from environment or fallback
const DEFAULT_API_URL = process.env.REACT_APP_API_URL || 'https://pacehere.com';

// Get API URL from localStorage if available, otherwise use environment variable
const getApiUrl = () => {
  try {
    // Try to get from the new settings structure
    const savedSettings = localStorage.getItem('api_settings');
    if (savedSettings) {
      const parsedSettings = JSON.parse(savedSettings);
      const activeId = parsedSettings.activeEndpointId;
      const activeEndpoint = parsedSettings.endpoints.find(e => e.id === activeId);
      if (activeEndpoint) {
        return activeEndpoint.url;
      }
    }
    
    // Fall back to legacy storage method
    const configuredUrl = localStorage.getItem('api_base_url');
    if (configuredUrl) {
      return configuredUrl;
    }
  } catch (error) {
    console.error('Error retrieving API URL from settings:', error);
  }
  
  // Default fallback
  return DEFAULT_API_URL;
};

// Create axios instance with dynamic base URL
const api = axios.create({
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add baseURL dynamically on each request
api.interceptors.request.use(config => {
  config.baseURL = getApiUrl();
  return config;
});

// Add auth interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/api/admin/auth/login', credentials),
  getProfile: () => api.get('/api/admin/auth/profile'),
  registerAdmin: (data) => api.post('/api/admin/auth/register', data)
};

// Admin API
export const adminAPI = {
  getAllAdmins: () => {
    return api.get('/api/admin/auth/admins');
  },
  
  registerAdmin: (adminData) => {
    return api.post('/api/admin/auth/register', adminData);
  },
  
  changeAdminRole: (adminId, roleData) => {
    return api.put(`/admin/auth/admins/${adminId}/role`, roleData);
  },
  
  deleteAdmin: (adminId) => {
    return api.delete(`/api/admin/auth/admins/${adminId}`);
  }
};

// Books API
export const booksAPI = {
  getAllBooks: (page = 1, limit = 10, status = '') => {
    let url = `/api/admin/books?page=${page}&limit=${limit}`;
    if (status) url += `&status=${status}`;
    return api.get(url);
  },
  getBookById: (id) => api.get(`/api/admin/books/${id}`),
  createBook: (bookData) => api.post('/api/admin/books', bookData),
  updateBook: (id, bookData) => api.put(`/api/admin/books/${id}`, bookData),
  deleteBook: (id) => api.delete(`/api/admin/books/${id}`),
  
  // Book text methods
  getBookTexts: (bookId) => api.get(`/api/admin/books/text/${bookId}`),
  getBookTextByType: (bookId, type, language = 'en') => 
    api.get(`/api/admin/books/text/${bookId}/type/${type}?language=${language}`),
  createBookText: (textData) => api.post('/api/admin/books/text', textData),
  updateBookText: (textId, textData) => api.put(`/api/admin/books/text/${textId}`, textData),
  deleteBookText: (textId) => api.delete(`/api/admin/books/text/${textId}`),
  
  // Text generation methods
  generateText: (data) => api.post('/api/admin/generate/text-openai', data),
  generateAudio: (data) => api.post('/api/admin/generate/audio-openai', data),
  generateGoogleAudio: (data) => api.post('/api/admin/generate/audio-google', data),
  generateLongformAudio: (data) => api.post('/api/admin/generate/longform-audio', data),
  
  // Add new method for long-form OpenAI audio generation
  generateLongFormOpenAIAudio: (data) => api.post('/api/admin/generate/audio-long-openai', data),
  
  // Update Vertex AI text generation to ensure parameters match API spec
  generateVertexText: (data) => api.post('/api/admin/generate/text-vertex', data),
  
  
  // Add method to fetch available TTS voices by language
  getAvailableTTSVoices: (languageCode = 'en-US') => 
    api.get(`/api/admin/generate/tts-voices?languageCode=${languageCode}`),
  
  // Cover upload method
  uploadCover: (id, formData) => api.post(`/api/admin/books/${id}/cover`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  
  // Add this new method for partial updates
  patchBook: (id, fieldData) => api.patch(`/api/admin/books/${id}`, fieldData),
  
  // Add these new methods for category management
  addCategoriesToBook: (bookId, categoryIds) => 
    api.post(`/api/admin/books/${bookId}/categories`, { categoryIds }),
  
  removeCategoriesFromBook: (bookId, categoryIds) => 
    api.delete(`/api/admin/books/${bookId}/categories`, { 
      data: { categoryIds } // For DELETE requests, data must be in this property
    }),
  
  // Add OpenLibrary-specific endpoint
  createBookFromOpenLibrary: (data) => api.post('/api/admin/books/magic/isbn-to-book/openlibrary', data),
  
  // Add Google Books-specific endpoint
  createBookFromGoogleBooks: (data) => api.post('/api/admin/books/magic/isbn-to-book/google', data),
  
  // Add Hybrid endpoint that combines multiple sources
  createBookFromHybridSources: (data) => api.post('/api/admin/books/magic/isbn-to-book/hybrid', data),
  
  // Add methods for similar books
  getSimilarBooks: (bookId) => api.get(`/api/books/${bookId}/similar`),
  updateSimilarBooks: (bookId, data) => api.post(`/api/admin/books/${bookId}/similar`, data),
  addSimilarBook: (bookId, data) => api.post(`/api/admin/books/${bookId}/similar/add`, data),
  removeSimilarBook: (bookId, similarBookId) => api.delete(`/api/admin/books/${bookId}/similar/${similarBookId}`),
  
  // Add the new structured text generation method
  generateStructuredText: (data) => api.post('/api/admin/generate/text-openai-structured', data),
  
  // Add the DeepSeek structured text generation method
  generateStructuredDeepseekText: (data) => api.post('/api/admin/generate/text-deepseek-structured', data),
  
  // Add method to find similar books using AI
  findSimilarBooks: (bookId) => api.get(`/api/admin/helpers/find-similar/${bookId}`),
  
  // Add Batch ISBN processing method
  createBooksFromBatchIsbns: (data) => api.post('/api/admin/books/magic/isbn-to-book/batch', data),
  
  // New method for uploading a book thumbnail
  uploadThumbnail: (bookId, formData) => api.post(`/api/admin/books/${bookId}/thumbnail`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  
  // Add URL-based cover upload methods
  uploadCoverByUrl: (id, urlData) => api.post(`/api/admin/books/${id}/coverByUrl`, urlData),
  uploadThumbnailByUrl: (id, urlData) => api.post(`/api/admin/books/${id}/thumbnailByUrl`, urlData),

  // Add translation methods
  getBookTranslations: (bookId) => api.get(`/api/admin/books/${bookId}/translations`),
  addTranslation: (bookId, data) => api.post(`/api/admin/books/${bookId}/translations`, data),
  updateTranslation: (bookId, translationId, data) => 
    api.put(`/api/admin/books/${bookId}/translations/${translationId}`, data),
  deleteTranslation: (bookId, translationId) => 
    api.delete(`/api/admin/books/${bookId}/translations/${translationId}`),
  translateBookContent: (originalBookId, translationBookId, language, type = 'short', provider = 'vertex-ai') => 
    api.post(`/api/admin/books/text/${originalBookId}/translate/${translationBookId}?type=${type}&language=${language}&provider=${provider}`),
};

// Users API
export const usersAPI = {
  getAllUsers: (page = 1, limit = 10, searchTerm = '', status = '', sortBy = 'createdAt') => {
    let url = `/api/admin/users?page=${page}&limit=${limit}`;
    if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
    if (status) url += `&status=${status}`;
    if (sortBy) url += `&sortBy=${sortBy}`;
    return api.get(url);
  },
  
  getUserById: (userId) => api.get(`/api/admin/users/${userId}`),
  
  updateUser: (userId, userData) => api.put(`/api/admin/users/${userId}`, userData),
  
  deleteUser: (userId) => api.delete(`/api/admin/users/${userId}`),
  
  getUserStatistics: () => api.get('/api/admin/users/statistics/overview')
};

// Categories API
export const categoriesAPI = {
  getAllCategories: () => api.get('/api/admin/categories'),
  createCategory: (categoryData) => api.post('/api/admin/categories', categoryData),
  updateCategory: (id, categoryData) => api.put(`/api/admin/categories/${id}`, categoryData),
  deleteCategory: (id) => api.delete(`/api/admin/categories/${id}`)
};

// Collections API
export const collectionsAPI = {
  getAllCollections: () => api.get('/api/admin/collections'),
  getCollectionById: (id) => api.get(`/api/admin/collections/${id}`),
  createCollection: (collectionData) => api.post('/api/admin/collections', collectionData),
  updateCollection: (id, collectionData) => api.put(`/api/admin/collections/${id}`, collectionData),
  deleteCollection: (id) => api.delete(`/api/admin/collections/${id}`),
  
  // Add specialized methods for managing books in collections
  addBooksToCollection: (collectionId, bookIds) => 
    api.post(`/api/admin/collections/${collectionId}/books`, { bookIds }),
  
  removeBooksFromCollection: (collectionId, bookIds) => 
    api.delete(`/api/admin/collections/${collectionId}/books`, { 
      data: { bookIds }  // For DELETE, the payload needs to be in the data property
    }),
  
  // Add method to generate collection cover
  generateCollectionCover: (collectionId, data) => 
    api.post(`/api/collections/${collectionId}/generate-cover`, data),
  
  // Update method to generate thematic collections using AI with user prompt
  generateCollections: (count = 5, prompt = '') => {
    let url = `/api/admin/helpers/generate-collections?count=${count}`;
    if (prompt) {
      url += `&prompt=${encodeURIComponent(prompt)}`;
    }
    return api.get(url);
  }
};

// Book of the Day API
export const bookOfTheDayAPI = {
  getAllEntries: (page = 1, limit = 10) => 
    api.get(`/api/admin/books-daily?page=${page}&limit=${limit}`),
  
  getByDate: (date) => 
    api.get(`/api/admin/books-daily/date/${date}`),
  
  createEntry: (entryData) => 
    api.post('/api/admin/books-daily', entryData),
  
  updateEntry: (id, entryData) => 
    api.put(`/api/admin/books-daily/${id}`, entryData),
  
  deleteEntry: (id) => 
    api.delete(`/api/admin/books-daily/${id}`),
  
  getFeaturedForToday: () =>
    api.get('/api/featured/book-of-the-day')
};

// Jobs API
export const jobsAPI = {
  getAllJobs: (page = 1, limit = 10, status = null) => {
    let url = `/api/admin/generate/jobs?page=${page}&limit=${limit}`;
    if (status) url += `&status=${status}`;
    return api.get(url);
  },
  getJobById: (jobId) => api.get(`/api/admin/generate/job/${jobId}`),
  getJobsByEntity: (entityId) => api.get(`/api/admin/generate/jobs/entity/${entityId}`)
};

// Courses API
export const coursesAPI = {
  getAllCourses: (status = '') => 
    api.get(`/api/admin/courses${status ? `?status=${status}` : ''}`),
  getCourseById: (id) => api.get(`/api/admin/courses/${id}`),
  createCourse: (courseData) => api.post('/api/admin/courses', courseData),
  updateCourse: (id, courseData) => api.put(`/api/admin/courses/${id}`, courseData),
  deleteCourse: (id) => api.delete(`/api/admin/courses/${id}`),
  
  // Themes
  getThemesForCourse: (courseId) => api.get(`/api/admin/courses/${courseId}/themes`),
  createTheme: (courseId, themeData) => api.post(`/api/admin/courses/${courseId}/themes`, themeData),
  updateTheme: (courseId, themeId, themeData) => 
    api.put(`/api/admin/courses/${courseId}/themes/${themeId}`, themeData),
  deleteTheme: (courseId, themeId) => 
    api.delete(`/api/admin/courses/${courseId}/themes/${themeId}`),
  reorderThemes: (courseId, themesOrder) => 
    api.post(`/api/admin/courses/${courseId}/themes/reorder`, themesOrder),
    
  // Blocks
  getBlocksForTheme: (themeId, level) => 
    api.get(`/api/admin/themes/${themeId}/blocks${level ? `?level=${level}` : ''}`),
  createBlock: (themeId, blockData) => 
    api.post(`/api/admin/themes/${themeId}/blocks`, blockData),
  updateBlock: (themeId, blockId, blockData) => 
    api.put(`/api/admin/themes/${themeId}/blocks/${blockId}`, blockData),
  deleteBlock: (themeId, blockId) => 
    api.delete(`/api/admin/themes/${themeId}/blocks/${blockId}`),
  reorderBlocks: (themeId, blocksOrder) => 
    api.post(`/api/admin/themes/${themeId}/blocks/reorder`, blocksOrder),
  
  // Add this if it's not already there:
  generateThemeBlocks: (data) => 
    api.post('/api/admin/courses-generate/theme-blocks', data),
  
  // Update to include model parameter
  generateCourse: (data) => api.post('/api/admin/courses-generate/vertex', data),
  
  // Add method to check job status
  checkJobStatus: (jobId) => api.get(`/api/admin/generate/job/${jobId}`),
  
  // Add this new method for generating course cover images
  generateCourseCovers: (courseId, options) => 
    api.post(`/api/admin/courses/${courseId}/generate-covers`, options),
  
  // Add these new methods for block audio generation
  generateBlockAudioGoogle: (data) => api.post('/api/admin/courses-generate/block-audio-google', data),
  generateBlockAudioOpenAI: (data) => api.post('/api/admin/courses-generate/block-audio-openai', data),
  
  // Add this new method for deleting block audio
  deleteBlockAudio: (blockId) => api.delete(`/api/admin/courses-generate/block-audio/${blockId}`),
};

// Search API
export const searchAPI = {
  search: (query, type = 'all', limit = 10) => 
    api.get(`/api/admin/search?q=${encodeURIComponent(query)}&type=${type}&limit=${limit}`),
  
  // Add specific Google Books search method
  searchGoogleBooks: (query, limit = 10) => 
    api.get(`/api/search/google?q=${encodeURIComponent(query)}&limit=${limit}`)
};

// Audio files API
export const audioFilesAPI = {
  getAllAudioFiles: (page = 1, limit = 10, search = '', type = '', provider = '', bookId = '') => {
    let url = `/api/admin/all-audio?page=${page}&limit=${limit}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (type) url += `&type=${type}`;
    if (provider) url += `&provider=${provider}`;
    if (bookId) url += `&bookId=${bookId}`;  // Add bookId parameter
    return api.get(url);
  },
  
  getAudioFileById: (id) => api.get(`/api/admin/audio/${id}`),
  
  deleteAudioFile: (id) => api.delete(`/api/admin/all-audio/${id}`)
};

export default api;
